"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Wrench, OctagonAlert as AlertOctagon, Droplets, ArrowRight, PhoneCall, Clock, ShieldCheck, CircleCheck as CheckCircle2 } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
const services = [{
  title: "Emergency Plumbing",
  badge: "24/7 Priority Response",
  badgeVariant: "destructive" as const,
  description: "Rapid on-site emergency service for overflowing toilets, burst pipes, and sudden catastrophic leaks in Jonesboro.",
  icon: AlertOctagon,
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/emergency_plumbing_service.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/emergency_plumbing_service.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/emergency_plumbing_service.png-wvc-sizes",
  features: ["Arrival in 60 mins or less", "24/7 live dispatch", "Immediate shut-off & repair"],
  link: "/services/",
  primaryCta: "Get Emergency Help"
}, {
  title: "Drain Cleaning",
  badge: "Same-Day Service",
  badgeVariant: "secondary" as const,
  description: "Professional hydro-jetting and motorized snaking to eliminate stubborn clogs, tree roots, and sewer backups.",
  icon: Droplets,
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/drain_cleaning_service.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/drain_cleaning_service.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/drain_cleaning_service.png-wvc-sizes",
  features: ["Video camera inspection", "Hydro-jet scouring", "Long-term clog prevention"],
  link: "/services/",
  primaryCta: "Clear My Drains"
}, {
  title: "Pipe Repair & Replacement",
  badge: "Permanent Fixes",
  badgeVariant: "secondary" as const,
  description: "Accurate leak detection, spot pipe repairs, repiping, and freeze damage restoration using durable materials.",
  icon: Wrench,
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/pipe_repair_service.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/pipe_repair_service.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/pipe_repair_service.png-wvc-sizes",
  features: ["Non-invasive leak locating", "Copper & PEX repiping", "Code-compliant warranty"],
  link: "/services/",
  primaryCta: "Inspect Pipes"
}];
export default function CoreServices() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.15
      }
    }
  };
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="coreservices" data-nav="light" className="bg-background py-16 md:py-24">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="mx-auto max-w-3xl text-center mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-secondary text-secondary-foreground text-xs font-semibold uppercase tracking-wider mb-4">
            <Clock className="size-3.5 text-primary" />
            <span>Fast Local Dispatch in Jonesboro, GA</span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold font-default text-foreground tracking-tight">
            Our Core Plumbing Services
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground">
            From sudden midnight disasters to stubborn clogged lines and damaged pipes, our licensed technicians resolve issues quickly with honest pricing.
          </p>
        </div>

        {/* Services Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.15
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {services.map((service, i) => {
          const IconComponent = service.icon;
          return <motion.div key={service.title} data-index={i} variants={cardVariants} className="flex flex-col h-full">
                <Card className="flex flex-col h-full overflow-hidden border border-border shadow-sm hover:shadow-md transition-[box-shadow,border-color] duration-300 hover:border-primary/40 bg-card text-card-foreground rounded-xl">
                  {/* Card Media Preview */}
                  <div className="relative h-48 sm:h-52 w-full overflow-hidden bg-muted">
                    <img src={service.image} data-wvc-srcset={service.imageSrcset} data-wvc-sizes={service.imageSizes} alt={service.title} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-500 ease-out hover:scale-105" />
                    <div className="absolute top-3 left-3 z-10">
                      <Badge variant={service.badgeVariant} className="shadow-xs font-medium">
                        {service.badge}
                      </Badge>
                    </div>
                    <div className="absolute bottom-3 right-3 size-10 rounded-lg bg-background/90 backdrop-blur-xs flex items-center justify-center text-primary shadow-xs">
                      <IconComponent className="size-5" />
                    </div>
                  </div>

                  <CardHeader className="pb-3">
                    <CardTitle className="text-xl sm:text-2xl font-bold font-default text-foreground">
                      {service.title}
                    </CardTitle>
                    <CardDescription className="text-muted-foreground text-sm leading-relaxed mt-2">
                      {service.description}
                    </CardDescription>
                  </CardHeader>

                  <CardContent className="flex-1 pb-4">
                    <div className="space-y-2.5 pt-2 border-t border-border/60">
                      {service.features.map((feature, featureIndex) => <div key={featureIndex} data-index={featureIndex} className="flex items-center gap-2.5 text-xs sm:text-sm text-foreground/80 font-medium">
                          <CheckCircle2 className="size-4 text-primary shrink-0" />
                          <span>{feature}</span>
                        </div>)}
                    </div>
                  </CardContent>

                  <CardFooter className="pt-2 flex flex-col gap-2.5">
                    <Button asChild className="w-full justify-between group rounded-md" size="default">
                      <Link to={service.link}>
                        <span>{service.primaryCta}</span>
                        <ArrowRight className="size-4 transition-transform duration-300 group-hover:translate-x-1" />
                      </Link>
                    </Button>
                    <div className="w-full flex items-center justify-between text-xs text-muted-foreground pt-1">
                      <span className="inline-flex items-center gap-1">
                        <ShieldCheck className="size-3.5 text-chart-4" />
                        Licensed & Insured
                      </span>
                      <span className="font-semibold text-foreground">Jonesboro, GA</span>
                    </div>
                  </CardFooter>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        {/* Bottom Contact Strip */}
        <div className="mt-12 md:mt-16 rounded-2xl bg-secondary p-6 sm:p-8 border border-border flex flex-col sm:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-4 text-center sm:text-left">
            <div className="size-12 rounded-xl bg-primary text-primary-foreground flex items-center justify-center shrink-0">
              <PhoneCall className="size-6" />
            </div>
            <div>
              <h3 className="text-base sm:text-lg font-bold font-default text-secondary-foreground">
                Need urgent plumbing service right now?
              </h3>
              <p className="text-xs sm:text-sm text-muted-foreground">
                Our Jonesboro emergency team is on standby 24 hours a day, 7 days a week.
              </p>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto shrink-0">
            <Button asChild variant="outline" className="w-full sm:w-auto rounded-md">
              <Link to="/services/">View All Services</Link>
            </Button>
            <Button asChild variant="default" className="w-full sm:w-auto rounded-md">
              <Link to="/contact/">Contact Dispatch</Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}